



































































































import Component, { mixins } from "vue-class-component";
import { InjectReactive, Prop, Provide, Watch } from "vue-property-decorator";
import { Call, CallStatus } from "@scrile/api-provider/dist/api/CallsProvider";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { isTipMessageDetails } from "@scrile/api-provider/dist/api/TipProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import WithAuthUser from "@/mixins/WithAuthUser";
import AudioPlayer from "@/lib/audioPlayer";
import { Debounce } from "@/lib/decorators";
import { getElement } from "@/lib/elementHelpers";
import emitter from "@/lib/emitter";
import config from "@/config";
import { EVENTS } from "@/const";
import { connectionStateEnum } from "@/const";
import BaseAvatar from "@/components/BaseAvatar";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import BaseIcon from "@/components/BaseIcon";
import MobileSwitchCam from "@/components/MobileSwitchCam";
import ModalView from "@/components/ModalView";
import TipIcon from "@/components/TipIcon";
import Tipping from "@/components/Tipping";
import { CallPrices } from "@/plugins/paidCalls/components/PaidCallController/PaidCallController.vue";
import CallStateView from "./CallStateView.vue";
import CallStatusView from "./CallStatusView";
import PaidVideoRequestConfirm from "./PaidVideoRequestConfirm.vue";
import TipMessage from "./TipMessage.vue";
import VideoRequestConfirm from "./VideoRequestConfirm";

const CLICK_DEBOUNCE_DELAY = 300;

@Component({
  name: "CallModalView",
  components: {
    PaidVideoRequestConfirm,
    VideoRequestConfirm,
    CallStatusView,
    CallStateView,
    ModalView,
    BaseIcon,
    BaseAvatar,
    BaseButton,
    BaseConfirm,
    MobileSwitchCam,
    TipIcon,
    Tipping,
    TipMessage,
  },
})
export default class CallModalView extends mixins(WithAuthUser) {
  @InjectReactive({
    from: "callPriceToProvide",
    default: () => ({
      currentCallPrice: 2,
      videoCallPrice: 4,
    }),
  })
  public readonly callPrices!: CallPrices;

  @InjectReactive({ from: "paidCallRequestData", default: () => ({ show: false, on: () => {} }) })
  public readonly paidCallRequestData!: { show: boolean; on: (v: boolean) => void };

  @Prop({ type: Boolean, default: false })
  public readonly showConnectionError!: boolean;

  @Prop({ type: String, default: "" })
  public readonly connectionErrorMessage!: string;

  @Prop({ required: true })
  public readonly opponentUser!: UserPublicData;

  @Prop({ required: true })
  public readonly call!: Call | null;

  @Prop({ required: true })
  public readonly opponentStream!: MediaStream;

  @Prop({ required: true })
  public readonly localStream!: MediaStream;

  @Prop({ type: Boolean, required: true })
  public localReadyVideoState!: boolean;

  @Prop({ type: Boolean, required: true })
  public opponentReadyVideoState!: boolean;

  @Prop({ type: Boolean, required: true })
  public readonly localVideoState!: boolean;

  @Prop({ type: Boolean, required: true })
  public opponentVideoState!: boolean;

  @Prop({ type: Boolean, default: false })
  public loading!: boolean;

  public showTippingModal = false;

  public tips: number[] = [];

  public get callerId(): string {
    return this.call?.callerId || "";
  }

  public get isAuthUserMember(): boolean {
    return checkRole(this.authUserStrict.role || "", config.userRegistrationRole);
  }

  @Prop({ type: String, default: () => connectionStateEnum.EMPTY })
  public readonly connectionState!: connectionStateEnum;

  public get isVideoStream(): boolean {
    return this.opponentVideoState && this.localVideoState;
  }

  public get processingVideoRequest(): boolean {
    return this.localReadyVideoState && !this.opponentReadyVideoState;
  }

  public get showConfirmVideoRequest(): boolean {
    return !this.localReadyVideoState && this.opponentReadyVideoState;
  }

  public get processingCall() {
    return this.call?.status === CallStatus.NEW || this.connectionState === connectionStateEnum.EMPTY;
  }

  public get iconVideo() {
    return this.isVideoStream ? "sci-video-off" : "sci-video";
  }

  public get showAvatar() {
    return (
      !this.isVideoStream &&
      !this.showConnectionError &&
      !this.processingVideoRequest &&
      !this.showConfirmVideoRequest &&
      !this.paidCallRequestData.show &&
      !this.processingCall &&
      ![connectionStateEnum.CONNECTING, connectionStateEnum.DISCONNECTED].includes(this.connectionState)
    );
  }

  public get showVideoButton() {
    return !this.processingVideoRequest && !this.processingCall && !this.callEnded && !this.showConnectionError;
  }

  public get showHangupButton() {
    return !this.callEnded || this.showConnectionError;
  }

  public get showTipButton() {
    return (
      config.featureTipping &&
      this.isAuthUserMember &&
      !this.processingCall &&
      !this.callEnded &&
      !this.showConnectionError
    );
  }

  public get callEnded() {
    return (
      (this.call && [CallStatus.CANCELLED, CallStatus.DECLINED, CallStatus.ENDED].includes(this.call.status)) ||
      !this.call
    );
  }

  public get localVideoTracks(): MediaStreamTrack[] {
    return this.localStream.getVideoTracks();
  }

  public get mirrorVideo() {
    const settings = this.localVideoTracks.length > 0 && this.localVideoTracks[0].getSettings();
    return !settings || !settings.facingMode || settings.facingMode === "user";
  }

  public get showSwitchCam() {
    return this.localVideoTracks.length > 0 && !this.processingVideoRequest && this.isVideoStream;
  }

  public get disabledVideoButton() {
    return this.loading || this.opponentReadyVideoState !== this.localReadyVideoState;
  }

  @Debounce(CLICK_DEBOUNCE_DELAY)
  public onVideoToggle() {
    this.$emit("toggleVideo");
  }

  @Debounce(CLICK_DEBOUNCE_DELAY)
  public onAgreeClick() {
    this.$emit("videoRequestAccept");
  }

  @Debounce(CLICK_DEBOUNCE_DELAY)
  public onChangeDeviceClick(constraints: MediaTrackConstraints) {
    if (!this.isVideoStream) {
      return;
    }
    this.$emit("changeDevice", constraints);
  }

  @Debounce(CLICK_DEBOUNCE_DELAY)
  public onCallStateViewClick() {
    this.showConnectionError ? this.$emit("callBack") : this.$emit("cancelVideoRequest");
  }

  @Debounce(CLICK_DEBOUNCE_DELAY)
  public onVideoRequestDeclineClick() {
    this.$emit("videoRequestDecline");
  }

  @Watch("opponentStream")
  public async onOpponentStreamChange(opponentStream: MediaStream) {
    const player = getElement(this.$refs.player) as HTMLVideoElement;
    player.pause();
    const hasMedia = opponentStream.getTracks().length > 0;
    if (hasMedia) {
      player.srcObject = opponentStream;
      await player.play();
    }
  }

  @Watch("localStream")
  public async onLocalStreamChange(localStream: MediaStream) {
    const player = getElement(this.$refs.localPlayer) as HTMLVideoElement;
    player.pause();
    const hasMedia = localStream.getVideoTracks().length > 0;
    if (hasMedia) {
      player.srcObject = localStream;
      await player.play();
    }
  }

  public receivedMessage(message: Message) {
    if (isTipMessageDetails(message.details) && "" + message.details.senderId === "" + this.opponentUser.id) {
      this.tips.unshift(message.details.amount);
      setTimeout(() => this.tips.pop(), 4000);
    }
  }

  @Provide("messageData")
  public providedData: { user: UserPublicData | null } = {
    user: null,
  };

  @Watch("opponentUser", { immediate: true })
  public onUserChanged(user: UserPublicData | null) {
    this.providedData.user = user;
  }

  public created() {
    emitter.on<any>(EVENTS.MESSAGES_NEW_MESSAGE, this.receivedMessage);
  }

  public beforeDestroy() {
    AudioPlayer.stop("beep");
    emitter.off<any>(EVENTS.MESSAGES_NEW_MESSAGE, this.receivedMessage);
  }
}
